<template>
  <div class="d-flex align-content-start adv_product_gallery">
    <div class="br-15 pos-rel product-page-single-img" v-if="mainMedia" :class="{ 'product-page-single-img--no-gallery': !galleryMedia || galleryMedia.length === 1 }">
      <slot name="badges" />
      <a
        v-if="mainMedia.type === 'image'"
        class="br-25 image-popup-vertical-fit"
        :href="mainMedia.large"
        :class="galleryMedia.length > 1 ? 'multiple_gallery_images' : ''"
        :aria-label="`${productCodeId}`">
        <adv-html-picture
          srcset-profile="from_w600"
          :srcset-image="mainMedia.original_raw"
          :width="360"
          :height="500"
          loading="lazy"
          :alt="`${productData.productName}`"
          img-class="imagepop img-fluid"
        />
      </a>
      <a v-else-if="mainMedia.type !== null && hasThumbnail(mainMedia.type)"
         href="#"
         @click.prevent="openVideoModal(mainMedia)"
         class="image-popup-vertical-fit">
        <img
          :src="resolveMediaThumbnail(mainMedia)"
          :width="600"
          :height="600"
          class="imagepop"
          :alt="`${productData.productName} Video`"
        />
        <img v-if="hasThumbnail(mainMedia.type)" src="/ui/main/img/video_cover.svg" alt="Play Video" class="video-overlay" />
      </a>
      <a
        v-else
        class="br-25 image-popup-vertical-fit"
        :href="'/ui/main/img/no_photo.webp'"
        :aria-label="`${productCodeId}`">
        <img
          :src="'/ui/main/img/no_photo.webp'"
          :width="360"
          :height="500"
          loading="lazy"
          class="imagepop img-fluid"
          :alt="`${productData.productName}`"
        />
      </a>
      <slot name="actions" />
    </div>

    <div class="prod-gallery-row-wrapper native_slider" v-if="galleryMedia && galleryMedia.length > 1">
      <button
        data-scroll_left_by="-1"
        aria-label="Previous"
        class="native_slider-btn native_slider-btn-prev"
      >Prev</button>
      <div class="prod-gallery-row native_slider-lg" data-drag_speed="2">
        <div class="prod-gallery-thumb native_slider-li"
             v-for="(media, key) in galleryMediaSorted"
             :key="key"
             @click="onMediaClick(media)"
        >
          <div class="thumbnail_container" @click="mainMedia = media">
            <div class="thumbnail">
              <AdvHtmlPicture
                v-if="media.type === 'image'"
                :srcset-image="media.original_raw"
                srcset-profile="from_w120"
                :width="120"
                :height="120"
                img-class="prod-gallery-thumb-img"
                :style="(mainMedia === media) ? '' : ''"
                :alt="`${productData.productName}`"
                :loading="'lazy'"
              />
              <img
                v-else
                :src="resolveMediaThumbnail(media)"
                :alt="`Video Thumbnail ${media.type}`"
                class="prod-gallery-thumb-img"
              />
              <img
                v-if="hasThumbnail(media.type)"
                src="/ui/main/img/video_cover.svg"
                alt="Video Overlay"
                class="video-overlay-thumb"
              />
            </div>
          </div>
        </div>
      </div>
      <button
        data-scroll_left_by="1"
        aria-label="Next"
        class="native_slider-btn native_slider-btn-next"
      >Next</button>
  </div>

    <div v-if="isModalOpen" class="modal-overlay" @click.self="closeVideoModal">
      <div class="modal-content">
        <button class="close-button" @click="closeVideoModal">×</button>
        <iframe
          v-if="currentVideo"
          :src="videoEmbedUrl"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowfullscreen
        ></iframe>
      </div>
    </div>
  </div>
</template>

<script>
import store from '../../vue/store'
import productMixin from './mixins/productMixins'
import AdvHtmlPicture from '../../vue/components/AdvHtmlPicture.vue'

export default {
  name: 'AdvProductGallery',
  components: {
    AdvHtmlPicture,
  },
  props: {
    productId: {
      type: Number,
      required: true,
    },
    mainImageFirst: {
      type: Boolean,
      required: false,
      default: false
    },
    freeEbook: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  mixins: [productMixin],
  data() {
    return {
      productCodeId: null,
      mainMedia: null,
      galleryMedia: [
        {
          is_main: '1',
          large: store.state.noImage,
          medium: store.state.noImage,
          original: store.state.noImage,
          original_raw: store.state.noImage,
          small: store.state.noImage,
          uri: '',
          type: 'image',
        },
      ],
      isModalOpen: false,
      currentVideo: null,
    }
  },
  computed: {
    selectedAttributeId () {
      return store.getters.getSelectedAttributeId
    },
    selectedProductCodeId () {
      return store.getters.getSelectedProductCodeId
    },
    galleryMediaSorted() {
      if (!this.mainImageFirst) {
        return this.galleryMedia
      }
      const images = [...this.galleryMedia]
      return images.sort(function (a, b) { return a.is_main === '1' ? -1 : b.is_main === '1' ? 1 : 0 })
    },
    videoEmbedUrl() {
      if (!this.currentVideo) return null;
      const videoType = this.currentVideo.type.toLowerCase();
      const videoId = this.currentVideo.uri;
      switch (videoType) {
        case 'youtube':
          return `https://www.youtube.com/embed/${videoId}`;
        case 'vimeo':
          return `https://player.vimeo.com/video/${videoId}`;
        case 'tiktok':
          return `https://www.tiktok.com/embed/v3/${videoId}`;
        case 'instagram':
          return `https://www.instagram.com/reel/${videoId}/embed/`;
        case 'facebook':
          return `https://www.facebook.com/plugins/video.php?href=https://www.facebook.com/watch?v=${videoId}`;
        default:
          return null;
      }
    },
  },
  watch: {
    selectedProductCodeId(val) {
      this.productCodeId = val
      this.setUpGalleryUpdate()
    },
    selectedAttributeId(attributeId) {
      const productCode = this.productData.productCodes.find(item =>
        item.productCodeAttributes.includes(attributeId)
      )
      if (productCode) {
        this.setUpGalleryUpdate(productCode.productCodeId);
      }
    },
  },
  mounted() {
    this.setProductCodeId()
    this.setUpGalleryUpdate()
  },
  methods: {
    setUpGalleryUpdate(codeId = null) {
      this.productCodeImages(codeId)
      this.setMainMedia()
    },
    setProductCodeId() {
      if (this.selectedProductCodeId) {
        this.productCodeId = this.selectedProductCodeId
      } else {
        const firstKey = Object.keys(this.productData.images)[0]
        this.productCodeId = firstKey ? parseInt(firstKey) : null
      }
    },
    productCodeImages(codeId = null) {
      if (codeId !== null) {
        this.galleryMedia = this.productData.images[parseInt(codeId)] || []
      } else if (this.productCodeId !== null) {
        this.galleryMedia = this.productData.images[this.productCodeId] || []
      } else {
        this.galleryMedia = []
      }
    },
    setMainMedia(media = null) {
      if (media) {
        this.mainMedia = media
      } else {
        this.mainMedia = this.galleryMedia.find(media => media.is_main === '1') || this.galleryMedia[0];
      }
    },
    isVideo(type) {
      return type !== 'image';
    },
    hasThumbnail(type) {
      return !['image', 'vimeo', 'tiktok', 'facebook', 'instagram'].includes(type);
    },
    openVideoModal(media) {
      if (this.isVideo(media.type)) {
        this.currentVideo = media;
        this.isModalOpen = true;
      }
    },
    closeVideoModal() {
      this.isModalOpen = false;
      this.currentVideo = null;
    },
    onMediaClick(media) {
      if (this.isVideo(media.type)) {
        this.openVideoModal(media);
      }

      if (this.hasThumbnail(media.type) || media.type === 'image') {
        this.setMainMedia(media);
      }
    },
    /**
     * Resolves the appropriate image URL for a media object.
     * For images, it returns the original raw URL.
     * For videos, it returns the constructed thumbnail URL based on the video type.
     * @param {Object} media - The media object.
     * @returns {String} - The resolved image URL.
     */
    resolveMediaThumbnail(media) {
      const videoType = media.type.toLowerCase();
      const videoId = media.uri;

      if (!videoType || !videoId) {
        return '/ui/main/img/no_video.jpg';
      }

      switch (videoType) {
        case 'youtube':
          return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
        case 'vimeo':
          return '/ui/main/img/no_video.jpg';
        case 'tiktok':
          return '/ui/main/img/no_video.jpg';
        case 'instagram':
          return '/ui/main/img/no_video.jpg';
        case 'facebook':
          return '/ui/main/img/no_video.jpg';
        default:
          return '/ui/main/img/no_photo.jpg';
      }
    },
  }
}
</script>

<style lang="scss" scoped>
.image-popup-vertical-fit {
  position: relative;
  picture {
    height: auto;
    width: auto;
    display: flex;
    position: relative;
  }
}
::v-deep .imagepop {
  object-fit: contain;
  display: flex;
  max-width: 100%;
  height: 100%;
  width: auto;
  @media screen and (min-width: 1400px){
    max-width: 360px;
  }
}
.product-page-single-img--no-gallery {
  flex: 0 0 100%;
  max-width: 100%;
}
.magnify-img {
  position: absolute;
  bottom: 15px;
  right: 15px;
  box-shadow: unset;
  background-color: transparent;
}
.multiple_gallery_images {
  @media screen and (max-width: 991px) {
    ::v-deep .imagepop {
      object-fit: cover;
      aspect-ratio: 3/4;
    }
  }
}
</style>
